import { createSelector } from "@reduxjs/toolkit";
import { selectProjectData } from "../../../../store/slices/projectSlice";
import { selectSendLists } from "../../../../store/slices/sendListsSlice";

const checkErrors = createSelector(
  [selectProjectData, selectSendLists],
  (projectData, sendListsData) => {
    const err = [];
    const handlers = [];
    const sendLists = []; // sendlists with errors

    // check the sendlists where there is an error
    sendListsData?.forEach((sendList) => {
      sendList.filters?.forEach((f) => {
        if (f.fields?.length === 0) {
          sendLists.push({
            sendList: { uuid: sendList.uuid },
            error: { field: "noField", text: "No fields chosen. " },
          });
        }
        if (
          f.valueType !== "now" &&
          f.valueType !== "fixed" &&
          !f.valueId?.trim()
        ) {
          sendLists.push({
            sendList: { uuid: sendList.uuid },
            error: { field: "noValue", text: "No value chosen. " },
          });
        }
        if (
          f.valueType !== "now" &&
          f.valueType === "fixed" &&
          f.values.length === 0
        ) {
          sendLists.push({
            sendList: { uuid: sendList.uuid },
            error: { field: "noValue", text: "No value chosen. " },
          });
        }
      });
    });

    // check the event handlers where there is a error
    projectData.EventHandlers?.forEach((eventHandler) => {
      eventHandler.Rules?.forEach((rule) => {
        // rule errors
        if (!rule.Actions?.filter((act) => act.Type).length) {
          handlers.push({
            eventHandler: { UUID: eventHandler.UUID },
            rule: { UUID: rule.UUID },
            action: null,
            error: {
              field: "actions",
              text: "At least one action is required.",
            },
          });
        }

        // check conditions
        (rule.Conditions || []).forEach((condition) => {
          // condition errors

          if (
            ["ResponderInContactSet", "StarterInContactSet"].includes(
              condition.Type
            ) &&
            !condition.TypeUUID
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              condition: { UUID: condition.UUID },
              error: {
                field: "missingConditionField",
                text: "Group not selected.",
              },
            });
          }

          if (
            ["CheckCounters", "CompareCounters"].includes(condition.Type) &&
            !(condition.Options || []).find(
              (condOpt) => condOpt.Type === "counter:category"
            )?.Value
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              condition: { UUID: condition.UUID },
              error: {
                field: "missingConditionField",
                text: "Counter not selected.",
              },
            });
          }
          if (
            ["isMilestone", "milestone:is-pending"].includes(condition.Type) &&
            !(condition.Options || []).find(
              (condOpt) => condOpt.Type === "isMilestone"
            )?.Value
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              condition: { UUID: condition.UUID },
              error: {
                field: "missingConditionField",
                text: "Milestone not selected.",
              },
            });
          }
          if (
            (["InputArgument", "InputArgumentDT"].includes(condition.Type) &&
              !condition.TypeUUID) ||
            ((condition.Options || []).find(
              (opt) => opt.Type === "dateTimeFrom"
            )?.Value === "inputArgument" &&
              !(condition.Options || []).find(
                (opt) => opt.Type === "dateTimeValue"
              )?.Value)
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              condition: { UUID: condition.UUID },
              error: {
                field: "missingConditionField",
                text: "Variable not selected.",
              },
            });
          }
          if (
            ["ticket:is-topic"].includes(condition.Type) &&
            (condition.Options || []).find((opt) => opt.Type === "Topic")
              ?.Value === "any" &&
            (condition.Options || []).find((opt) => opt.Type === "TicketStatus")
              ?.Value === "any"
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              condition: { UUID: condition.UUID },
              error: {
                field: "topicStatusSame",
                text: "You need to select at least one of the topics or statuses.",
              },
            });
          }
        });

        // check actions
        rule.Actions?.forEach((action) => {
          if (
            (action.Options || []).find((actOpt) => actOpt.Type === "storeAs")
              ?.Value === "storageRecipient:category" &&
            (action.Options || []).filter(
              (actOpt) => actOpt.Type === "validateAs"
            ).length === 0
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "validateAs",
                text: "At least one validation is required for recipient storage.",
              },
            });
          }

          if (action.Type === "start:broadcast" && !action.TypeUUID) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID, Type: action.Type },
              configuration: true, // show error in the configuration tab
              error: {
                field: "MessageTemplateUUID",
                text: "No message is defined.",
              },
            });
          }

          if (
            (action.Type === "RemoveAllFromContactSet" ||
              action.Type === "AddResponderToContactSet" ||
              action.Type === "RemoveResponderFromContactSet" ||
              action.Type === "AddDynamicContactSetToContactSet" ||
              action.Type === "RemoveDynamicContactSetFromContactSet" ||
              action.Type === "AddStarterToContactSet" ||
              action.Type === "RemoveStarterFromContactSet") &&
            (action.Options || []).filter(
              (actOpt) => actOpt.Type === "group" && !!actOpt.Value
            ).length === 0
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "noContactSet",
                text: "Group not defined.",
              },
            });
          }

          if (
            action.Type === "start:broadcast" &&
            (action.Options || []).some(
              (opt) => opt.Type === "mode" && opt.Value === "channel"
            ) &&
            (action.Options || []).filter((actOpt) => actOpt.Type === "channel")
              .length === 0
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "channel",
                text: "No comms channel is defined.",
              },
            });
          }

          if (
            action.Type === "start:broadcast" &&
            (action.Options || []).some(
              (opt) => opt.Type === "mode" && opt.Value === "contactField"
            ) &&
            (action.Options || []).filter(
              (actOpt) => actOpt.Type === "contactFieldUUID"
            ).length === 0
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "contactFieldUUID",
                text: "No contact field is defined.",
              },
            });
          }

          if (
            action.Type === "customEvent" &&
            (action.Options || []).filter(
              (actOpt) => actOpt.Type === "customEvent"
            ).length === 0
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "noCustomEvent",
                text: "No custom event is defined.",
              },
            });
          }

          if (
            action.Type === "SetCounter" &&
            !(action.Options || []).find((actOpt) => actOpt.Type === "variable")
              ?.Value
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "variable",
                text: "No number variable is selected.",
              },
            });
          }

          if (
            (action.Type === "setMilestone" ||
              (action.Type === "start:broadcast" && action.ExpectReplies)) &&
            (action.Options || []).filter(
              (actOpt) => actOpt.Type === "MilestoneLabel"
            ).length === 0
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "MilestoneLabel",
                text: "Milestone label is not defined.",
              },
            });
          }

          if (
            action.Type === "cancelMilestone" &&
            (action.Options || []).filter(
              (actOpt) => actOpt.Type === "withLabel" && actOpt.Value === "true"
            ).length > 0 &&
            (action.Options || []).filter(
              (actOpt) => actOpt.Type === "isMilestone"
            ).length === 0
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "isMilestone",
                text: "Milestone label is not defined.",
              },
            });
          }

          if (
            action.Type === "form-question" &&
            action.Options?.find((opt) => opt.Type === "Type")?.Value ===
              "file" &&
            (action.Options || []).filter(
              (actOpt) => actOpt.Type === "FileType"
            ).length === 0
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "noFileType",
                text: "File type is not selected.",
              },
            });
          }
          if (
            action.Type === "form-question" &&
            (action.Options || []).filter((actOpt) => actOpt.Type === "Title")
              .length === 0
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "Title",
                text: "Question title is missing.",
              },
            });
          }

          if (
            action.Type === "StoreInputArgumentToDynamicContactSet" &&
            !(action.Options || []).find((actOpt) => actOpt.Type === "variable")
              ?.Value
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "variable",
                text: "No variable is selected.",
              },
            });
          }

          if (
            action.Type === "ticket:set" &&
            !(action.Options || []).find((actOpt) => actOpt.Type === "ticket")
              ?.Value
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "ticket",
                text: "No ticket reference is selected.",
              },
            });
          }

          if (
            action.Type === "ticket:set-status" &&
            !(action.Options || []).find((actOpt) => actOpt.Type === "status")
              ?.Value
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "status",
                text: "No status is selected.",
              },
            });
          }

          if (
            action.Type === "ticket:set-status" &&
            !(action.Options || []).find((actOpt) => actOpt.Type === "ticket")
              ?.Value
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "ticket",
                text: "No ticket is selected.",
              },
            });
          }

          if (
            action.Type === "contact:load" &&
            !(action.Options || []).find(
              (actOpt) => actOpt.Type === "contactFieldUuid"
            )?.Value
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "contactFieldUuid",
                text: "No contact field is selected.",
              },
            });
          }

          if (
            action.Type === "contact:first-or-create" &&
            !(action.Options || []).find((actOpt) => actOpt.Type === "value")
              ?.Value
          ) {
            handlers.push({
              eventHandler: { UUID: eventHandler.UUID },
              rule: { UUID: rule.UUID },
              action: { UUID: action.UUID },
              error: {
                field: "value",
                text: "No contact is selected.",
              },
            });
          }
        });
      });
    });

    // check the workflows
    projectData.Workflows?.forEach((workflow) => {
      // no listener
      if (!workflow.EventListeners?.length) {
        err.push({
          workflow: { UUID: workflow.UUID, Label: workflow.Label },
          eventListener: { EventType: "runtime:start" },
          eventHandler: {},
          rule: null,
          action: null,
          error: {
            field: "noListener",
            text: "No listener is defined.",
          },
        });
      }

      // sendlists in workflows
      sendLists.forEach((sl) => {
        if (sl.sendList.uuid === workflow.UUID) {
          err.push({
            ...sl,
            workflow: { UUID: workflow.UUID, Label: workflow.Label },
          });
        }
      });

      // milestone in runtime-start for form-render
      if (
        workflow.EventListeners?.some((el) => el.EventType === "form-render")
      ) {
        const runtimeStartListeners =
          workflow.EventListeners?.filter(
            (el) => el.EventType === "runtime:start"
          ) ?? [];
        const runtimeStartHandlers = runtimeStartListeners.map(
          (rl) =>
            projectData.EventHandlers.find(
              (eh) => eh.UUID === rl.EventHandlerUUID
            ) || {}
        );

        if (
          !runtimeStartHandlers?.some((rh) =>
            rh.Rules?.some((rhr) =>
              rhr?.Actions?.some((rhra) => rhra.Type === "setMilestone")
            )
          )
        ) {
          err.push({
            workflow: { UUID: workflow.UUID, Label: workflow.Label },
            EventType: "runtime:start",
            eventListener: { EventType: "runtime:start" },
            error: {
              field: "noMilestoneForFormRender",
              text: "Please use 'Set milestone' action to give your recipients time to reply to your survey.",
            },
          });
        }
      }

      workflow.EventListeners?.forEach((eventListener) => {
        if (!eventListener.EventHandlerUUID) {
          err.push({
            workflow: { UUID: workflow.UUID, Label: workflow.Label },

            eventListener: {
              UUID: eventListener.UUID,
              EventType: eventListener.EventType,
            },
            EventType: eventListener.EventType,
            error: {
              field: "noEventListener",
              text: "Event listener not complete.",
            },
          });
        }
        handlers.forEach((handler) => {
          if (
            handler.eventHandler.UUID === eventListener.EventHandlerUUID &&
            // ignore no message error if message is in the workflow
            !(
              handler.error.field === "MessageTemplateUUID" &&
              handler.action.Type === "start:broadcast" &&
              workflow.MessageTemplateUUID
            )
          ) {
            err.push({
              workflow: { UUID: workflow.UUID, Label: workflow.Label },
              ...(handler.configuration
                ? { error: handler.error, configuration: true }
                : {
                    eventListener: {
                      UUID: eventListener.UUID,
                      EventType: eventListener.EventType,
                    },
                    ...handler,
                  }),
            });
          }
        });
      });
    });

    projectData.MessageTemplates?.forEach((messageTemplate) => {
      sendLists.forEach((sl) => {
        if (sl.sendList.uuid === messageTemplate.UUID) {
          err.push({
            ...sl,
            messageTemplate: {
              UUID: messageTemplate.UUID,
              Label: messageTemplate.Label,
            },
          });
        }
      });
    });

    return err;
  }
);

export default checkErrors;
